import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE_URL } from '../App';

const SATELLITE_TILES =
	'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const US_STATES_GEOJSON = `${process.env.PUBLIC_URL || ''}/assets/costco/us-states.geojson`;

const COLORS = {
	red: '#E31837',
	blue: '#005DAA',
	visitedFill: 'rgba(227, 24, 55, 0.28)',
	defaultFill: 'rgba(0, 93, 170, 0.12)',
	pin: '#E31837',
	pinVisited: '#005DAA',
};

const DEFAULT_ZOOM = 4;

function zoomToLeafletLevel(zoom) {
	if (zoom == null) return DEFAULT_ZOOM;
	return Math.round(Math.min(12, Math.max(3, 2 + zoom * 2.2)));
}

function MapViewController({ center, zoom }) {
	const map = useMap();
	useEffect(() => {
		if (!center) return;
		const latLng = [center[1], center[0]];
		const z = zoomToLeafletLevel(zoom);
		map.flyTo(latLng, z, { duration: 0.55 });
	}, [center, zoom, map]);
	return null;
}

function CostcoMap({
	visitedIds,
	warehouses,
	selectedId,
	highlightId,
	mapCenter,
	mapZoom,
	onSelectPin,
	onHoverPin,
}) {
	const [voronoi, setVoronoi] = useState(null);
	const [statesGeo, setStatesGeo] = useState(null);
	const visited = useMemo(() => new Set(visitedIds || []), [visitedIds]);

	useEffect(() => {
		let cancelled = false;
		const staticUrl = `${process.env.PUBLIC_URL || ''}/assets/costco/voronoi-us.json`;
		fetch(`${API_BASE_URL}/costco-voronoi?country=US`)
			.then((r) => (r.ok ? r.json() : fetch(staticUrl).then((s) => s.json())))
			.then((data) => {
				if (!cancelled) setVoronoi(data);
			})
			.catch(() =>
				fetch(staticUrl)
					.then((r) => r.json())
					.then((data) => {
						if (!cancelled) setVoronoi(data);
					}),
			);
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		fetch(US_STATES_GEOJSON)
			.then((r) => r.json())
			.then((data) => {
				if (!cancelled) setStatesGeo(data);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	const centerLngLat = mapCenter || [-96.5, 39.5];
	const initialCenter = [centerLngLat[1], centerLngLat[0]];
	const initialZoom = zoomToLeafletLevel(mapZoom ?? 1);

	const visiblePins = useMemo(() => {
		if (!warehouses?.length) return [];
		const z = mapZoom ?? 1;
		if (z >= 2.5) return warehouses;
		if (z >= 1.8) return warehouses.filter((_, i) => i % 2 === 0 || visited.has(warehouses[i]?.id));
		return warehouses.filter((w) => visited.has(w.id));
	}, [warehouses, mapZoom, visited]);

	const voronoiStyle = (feature) => {
		const wid = feature?.properties?.warehouseId;
		const isVisited = visited.has(wid);
		const isHighlight = highlightId === wid;
		return {
			fillColor: isVisited ? COLORS.red : COLORS.blue,
			fillOpacity: isVisited ? 0.32 : 0.14,
			color: isHighlight ? COLORS.red : 'rgba(255, 255, 255, 0.55)',
			weight: isHighlight ? 1.4 : 0.45,
			interactive: false,
		};
	};

	return (
		<div className="costco-map-wrap costco-map-wrap--leaflet">
			<MapContainer
				center={initialCenter}
				zoom={initialZoom}
				minZoom={3}
				maxZoom={12}
				className="costco-leaflet-map"
				zoomControl
				scrollWheelZoom
				worldCopyJump
			>
				<TileLayer
					url={SATELLITE_TILES}
					attribution="Tiles &copy; Esri &mdash; Maxar, Earthstar Geographics"
					maxZoom={19}
				/>
				{statesGeo && (
					<GeoJSON
						data={statesGeo}
						style={{
							fillColor: 'transparent',
							fillOpacity: 0,
							color: '#ffffff',
							weight: 1.2,
							opacity: 0.85,
							interactive: false,
						}}
					/>
				)}
				{voronoi && <GeoJSON data={voronoi} style={voronoiStyle} />}
				{visiblePins.map((w) => {
					const isVisited = visited.has(w.id);
					const isSelected = selectedId === w.id;
					const radius = isSelected ? 9 : isVisited ? 7 : 6;
					return (
						<CircleMarker
							key={w.id}
							center={[w.lat, w.lng]}
							radius={radius}
							pathOptions={{
								fillColor: isVisited ? COLORS.pinVisited : COLORS.pin,
								fillOpacity: 1,
								color: '#ffffff',
								weight: isSelected ? 3 : 2,
							}}
							eventHandlers={{
								click: () => onSelectPin?.(w),
								mouseover: () => onHoverPin?.(w.id),
								mouseout: () => onHoverPin?.(null),
							}}
						/>
					);
				})}
				<MapViewController center={centerLngLat} zoom={mapZoom} />
			</MapContainer>
			<p className="costco-map-legend">
				Satellite basemap with US outline. Territories are approximate — your log stays on the pin.
			</p>
		</div>
	);
}

export default CostcoMap;
